import { Component, ViewChild, ElementRef, Output, EventEmitter, Input } from '@angular/core';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-image-cropper',
  standalone: false,
  templateUrl: './image-cropper.component.html',
  styleUrl: './image-cropper.component.scss'
})
export class ImageCropperComponent {
  @ViewChild('cropperCanvas') cropperCanvas!: ElementRef<HTMLCanvasElement>;

  /**
   * Crop mode:
   *   'square'  — fixed 1:1 crop box (default)
   *   'free'    — user resizes width and height independently
   *   'banner'  — fixed 1920×872 crop box for cover photos
   */
  @Input() cropMode: 'square' | 'free' | 'banner' = 'square';

  /**
   * Output size of the cropped image in pixels.
   * square: output is outputSize × outputSize.
   * free: longest dimension equals outputSize, other is proportional.
   * banner: ignored — output is always 1920×872.
   */
  @Input() outputSize: number = 400;

  /**
   * JPEG/WebP quality for the output image (0-1). Only applies to lossy formats.
   * Ignored for lossless formats like PNG.
   */
  @Input() quality: number = 1.0;

  /**
   * Title displayed in the cropper modal header
   */
  @Input() title: string = 'Crop Image';

  /**
   * Hint text displayed below the canvas
   */
  @Input() hintText: string = 'Grab and drag crop box to move • Drag handles to resize';

  /**
   * Uploading text displayed on the save button while upload is in progress
   */
  @Input() uploadingText: string = '';

  /**
   * Optional upload function to call when user saves. If provided, the cropper will
   * show loading state and only close on successful upload.
   * Should return an Observable that completes on success or errors on failure.
   */
  @Input() uploadFn?: (blob: Blob) => Observable<any>;

  private readonly BANNER_RATIO = 1920 / 872;
  private readonly BANNER_OUT_WIDTH = 1920;
  private readonly BANNER_OUT_HEIGHT = 872;

  /**
   * Emitted when the user applies the crop with the cropped image data URL
   * Only emitted if no uploadFn is provided
   */
  @Output() imageCropped = new EventEmitter<string>();

  /**
   * Emitted when the user applies the crop with the cropped image as a Blob
   * Only emitted if no uploadFn is provided
   */
  @Output() imageCroppedBlob = new EventEmitter<Blob>();

  /**
   * Emitted when upload succeeds (only if uploadFn is provided)
   */
  @Output() uploadSuccess = new EventEmitter<any>();

  /**
   * Emitted when upload fails (only if uploadFn is provided)
   */
  @Output() uploadError = new EventEmitter<any>();

  /**
   * Emitted when the cropper is closed (either by applying or canceling)
   */
  @Output() closed = new EventEmitter<void>();

  // Cropper visibility state
  isVisible = false;

  // Upload state
  isUploading = false;

  // Image data
  private imageSource: string = '';
  private imageSourceType: string = 'image/jpeg';
  private currentImage: HTMLImageElement | null = null;

  // Canvas state
  private isDragging = false;
  private dragStart = { x: 0, y: 0 };
  private imagePosition = { x: 0, y: 0 };
  private imageScale = 1;

  // Crop box state — width and height decouple to support free-aspect-ratio mode.
  // In square mode, width === height at all times.
  private cropBox = { x: 100, y: 100, width: 200, height: 200 };
  private dragMode: 'none' | 'cropbox' | 'resize' = 'none';
  private resizeHandle: string | null = null;

  // Current cursor style
  currentCursor: string = 'default';

  /**
   * Open the cropper with an image
   * @param imageDataUrl - Base64 data URL of the image to crop
   */
  open(imageDataUrl: string): void {
    this.imageSource = imageDataUrl;
    const match = imageDataUrl.match(/^data:([^;]+);/);
    this.imageSourceType = match?.[1] ?? 'image/jpeg';
    this.isVisible = true;
    this.resetCropperState();
    this.initCropperCanvas();
  }

  /**
   * Close the cropper without applying
   */
  close(): void {
    this.isVisible = false;
    this.imageSource = '';
    this.currentImage = null;
    this.closed.emit();
  }

  /**
   * Reset cropper state
   */
  private resetCropperState(): void {
    this.imagePosition = { x: 0, y: 0 };
    this.imageScale = 1;
    this.isDragging = false;
    this.dragMode = 'none';
    this.resizeHandle = null;
    this.cropBox = { x: 100, y: 100, width: 200, height: 200 };
  }

  /**
   * Initialize cropper canvas
   */
  initCropperCanvas(): void {
    setTimeout(() => {
      const canvas = this.cropperCanvas?.nativeElement;
      if (!canvas || !this.imageSource) {
        console.warn('Canvas or image not available');
        return;
      }

      const img = new Image();
      img.onload = () => {
        this.currentImage = img;
        const canvasSize = canvas.width;

        // Display image at original size, or scale down if too large to fit
        const scaleX = canvasSize / img.width;
        const scaleY = canvasSize / img.height;
        const scale = Math.min(1, Math.min(scaleX, scaleY)); // Scale down only if needed, never up
        this.imageScale = scale;

        // Center the image
        this.imagePosition = {
          x: (canvasSize - img.width * scale) / 2,
          y: (canvasSize - img.height * scale) / 2
        };

        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;

        if (this.cropMode === 'free') {
          // Free mode: initial crop box covers the entire image
          this.cropBox = {
            x: this.imagePosition.x,
            y: this.imagePosition.y,
            width: scaledWidth,
            height: scaledHeight,
          };
        } else if (this.cropMode === 'banner') {
          // Banner mode: fit the largest 1920×872 crop box centered in the image
          let cropWidth: number;
          let cropHeight: number;
          if (scaledWidth / scaledHeight >= this.BANNER_RATIO) {
            cropHeight = scaledHeight;
            cropWidth = cropHeight * this.BANNER_RATIO;
          } else {
            cropWidth = scaledWidth;
            cropHeight = cropWidth / this.BANNER_RATIO;
          }
          this.cropBox = {
            x: this.imagePosition.x + (scaledWidth - cropWidth) / 2,
            y: this.imagePosition.y + (scaledHeight - cropHeight) / 2,
            width: cropWidth,
            height: cropHeight,
          };
        } else {
          // Square mode: center the largest square crop box
          const cropSize = Math.min(scaledWidth, scaledHeight);
          this.cropBox = {
            x: this.imagePosition.x + (scaledWidth - cropSize) / 2,
            y: this.imagePosition.y + (scaledHeight - cropSize) / 2,
            width: cropSize,
            height: cropSize,
          };
        }

        this.drawCropper();
      };

      img.onerror = (error) => {
        console.error('Failed to load image:', error);
      };

      img.src = this.imageSource;
    }, 100);
  }

  /**
   * Draw the cropper canvas
   */
  private drawCropper(): void {
    const canvas = this.cropperCanvas?.nativeElement;
    if (!canvas || !this.currentImage) {
      console.warn('Canvas or image not ready for drawing');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const canvasSize = canvas.width;

    // Clear canvas with black background
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvasSize, canvasSize);

    // Draw the image at current position and scale
    ctx.save();
    ctx.drawImage(
      this.currentImage,
      this.imagePosition.x,
      this.imagePosition.y,
      this.currentImage.width * this.imageScale,
      this.currentImage.height * this.imageScale
    );
    ctx.restore();

    // Draw semi-transparent overlay over entire canvas
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvasSize, canvasSize);

    const { x, y, width, height } = this.cropBox;

    // Clear the crop box area to show the image clearly
    ctx.clearRect(x, y, width, height);

    // Redraw the image in the crop box area
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, width, height);
    ctx.clip();
    ctx.drawImage(
      this.currentImage,
      this.imagePosition.x,
      this.imagePosition.y,
      this.currentImage.width * this.imageScale,
      this.currentImage.height * this.imageScale
    );
    ctx.restore();

    // Draw crop box border
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, width, height);

    // Draw resize handles at corners and edges
    this.drawResizeHandles(ctx);
  }

  /**
   * Draw resize handles for the crop box
   */
  private drawResizeHandles(ctx: CanvasRenderingContext2D): void {
    // Larger handles on mobile for better touch interaction
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const handleSize = isMobile ? 16 : 10;
    const { x, y, width, height } = this.cropBox;

    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = isMobile ? 2 : 1;

    // Corner handles
    const corners = [
      { x: x,         y: y          }, // top-left
      { x: x + width, y: y          }, // top-right
      { x: x,         y: y + height }, // bottom-left
      { x: x + width, y: y + height }, // bottom-right
    ];

    // Edge handles
    const edges = [
      { x: x + width / 2, y: y             }, // top
      { x: x + width / 2, y: y + height    }, // bottom
      { x: x,             y: y + height / 2 }, // left
      { x: x + width,     y: y + height / 2 }, // right
    ];

    // Draw all handles
    [...corners, ...edges].forEach(handle => {
      ctx.fillRect(handle.x - handleSize / 2, handle.y - handleSize / 2, handleSize, handleSize);
      ctx.strokeRect(handle.x - handleSize / 2, handle.y - handleSize / 2, handleSize, handleSize);
    });
  }

  /**
   * Handle mouse down on canvas
   */
  onCanvasMouseDown(event: MouseEvent): void {
    const canvas = this.cropperCanvas?.nativeElement;
    if (!canvas) return;

    // Scale mouse coordinates from displayed size to canvas internal size
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const mouseX = event.offsetX * scaleX;
    const mouseY = event.offsetY * scaleY;

    // Check if clicking on a resize handle
    const handle = this.getResizeHandle(mouseX, mouseY);
    if (handle) {
      this.isDragging = true;
      this.dragMode = 'resize';
      this.resizeHandle = handle;
      this.dragStart = { x: mouseX, y: mouseY };
      this.updateCursor(mouseX, mouseY);
      return;
    }

    // Check if clicking inside crop box
    if (this.isInsideCropBox(mouseX, mouseY)) {
      this.isDragging = true;
      this.dragMode = 'cropbox';
      this.dragStart = {
        x: mouseX - this.cropBox.x,
        y: mouseY - this.cropBox.y
      };
      this.updateCursor(mouseX, mouseY);
      return;
    }

    // Do nothing if clicking outside crop box (image is not draggable)
  }

  /**
   * Check if point is inside crop box
   */
  private isInsideCropBox(x: number, y: number): boolean {
    return x >= this.cropBox.x && x <= this.cropBox.x + this.cropBox.width &&
           y >= this.cropBox.y && y <= this.cropBox.y + this.cropBox.height;
  }

  /**
   * Get resize handle at mouse/touch position
   * @param x - X coordinate
   * @param y - Y coordinate
   * @param touchMode - If true, uses larger hit area for touch interactions
   */
  private getResizeHandle(x: number, y: number, touchMode: boolean = false): string | null {
    const handleSize = 10;
    // Use much larger threshold for touch to make it easier to tap handles on mobile
    const threshold = touchMode ? 22 : handleSize / 2;
    const { x: bx, y: by, width, height } = this.cropBox;

    const handles = [
      { name: 'nw', x: bx,             y: by              },
      { name: 'ne', x: bx + width,     y: by              },
      { name: 'sw', x: bx,             y: by + height     },
      { name: 'se', x: bx + width,     y: by + height     },
      { name: 'n',  x: bx + width / 2, y: by              },
      { name: 's',  x: bx + width / 2, y: by + height     },
      { name: 'w',  x: bx,             y: by + height / 2 },
      { name: 'e',  x: bx + width,     y: by + height / 2 },
    ];

    for (const handle of handles) {
      if (Math.abs(x - handle.x) <= threshold && Math.abs(y - handle.y) <= threshold) {
        return handle.name;
      }
    }

    return null;
  }

  /**
   * Get appropriate cursor style for resize handle
   */
  private getCursorForHandle(handle: string): string {
    const cursors: { [key: string]: string } = {
      'nw': 'nwse-resize',
      'ne': 'nesw-resize',
      'sw': 'nesw-resize',
      'se': 'nwse-resize',
      'n': 'ns-resize',
      's': 'ns-resize',
      'w': 'ew-resize',
      'e': 'ew-resize'
    };
    return cursors[handle] || 'default';
  }

  /**
   * Update cursor based on mouse position
   */
  private updateCursor(x: number, y: number): void {
    if (this.isDragging) {
      if (this.dragMode === 'cropbox') {
        this.currentCursor = 'grabbing';
      } else if (this.dragMode === 'resize' && this.resizeHandle) {
        this.currentCursor = this.getCursorForHandle(this.resizeHandle);
      }
      return;
    }

    const handle = this.getResizeHandle(x, y);
    if (handle) {
      this.currentCursor = this.getCursorForHandle(handle);
      return;
    }

    if (this.isInsideCropBox(x, y)) {
      this.currentCursor = 'grab';
      return;
    }

    this.currentCursor = 'default';
  }

  /**
   * Handle mouse move over canvas (for cursor updates)
   */
  onCanvasMouseHover(event: MouseEvent): void {
    const canvas = this.cropperCanvas?.nativeElement;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const mouseX = event.offsetX * scaleX;
    const mouseY = event.offsetY * scaleY;

    this.updateCursor(mouseX, mouseY);
  }

  /**
   * Handle mouse move on canvas
   */
  onCanvasMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;

    const canvas = this.cropperCanvas?.nativeElement;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const mouseX = (event.clientX - rect.left) * scaleX;
    const mouseY = (event.clientY - rect.top) * scaleY;

    if (this.dragMode === 'cropbox') {
      this.dragCropBox(mouseX, mouseY);
    } else if (this.dragMode === 'resize' && this.resizeHandle) {
      this.resizeCropBox(mouseX, mouseY);
    }

    this.drawCropper();
  }

  /**
   * Move the crop box, constrained within image boundaries
   */
  private dragCropBox(mouseX: number, mouseY: number): void {
    if (!this.currentImage) return;

    let newX = mouseX - this.dragStart.x;
    let newY = mouseY - this.dragStart.y;

    const imgLeft = this.imagePosition.x;
    const imgTop = this.imagePosition.y;
    const imgRight = this.imagePosition.x + this.currentImage.width * this.imageScale;
    const imgBottom = this.imagePosition.y + this.currentImage.height * this.imageScale;

    newX = Math.max(imgLeft, Math.min(newX, imgRight - this.cropBox.width));
    newY = Math.max(imgTop, Math.min(newY, imgBottom - this.cropBox.height));

    this.cropBox.x = newX;
    this.cropBox.y = newY;
  }

  private resizeCropBox(mouseX: number, mouseY: number): void {
    if (!this.currentImage) return;

    if (this.cropMode === 'free') {
      this.resizeCropBoxFree(mouseX, mouseY);
    } else if (this.cropMode === 'banner') {
      this.resizeCropBoxLocked(mouseX, mouseY, this.BANNER_RATIO);
    } else {
      this.resizeCropBoxSquare(mouseX, mouseY);
    }
  }

  /**
   * Free-aspect-ratio resize: width and height are independent.
   */
  private resizeCropBoxFree(mouseX: number, mouseY: number): void {
    const minSize = 50;
    const { x, y, width, height } = this.cropBox;
    const handle = this.resizeHandle;

    const imgLeft = this.imagePosition.x;
    const imgTop = this.imagePosition.y;
    const imgRight = this.imagePosition.x + this.currentImage!.width * this.imageScale;
    const imgBottom = this.imagePosition.y + this.currentImage!.height * this.imageScale;

    let newX = x;
    let newY = y;
    let newWidth = width;
    let newHeight = height;

    const rightEdge = x + width;
    const bottomEdge = y + height;

    if (handle === 'se') {
      newWidth = Math.max(minSize, Math.min(mouseX - x, imgRight - x));
      newHeight = Math.max(minSize, Math.min(mouseY - y, imgBottom - y));
    } else if (handle === 'sw') {
      newWidth = Math.max(minSize, Math.min(rightEdge - mouseX, rightEdge - imgLeft));
      newHeight = Math.max(minSize, Math.min(mouseY - y, imgBottom - y));
      newX = rightEdge - newWidth;
    } else if (handle === 'ne') {
      newWidth = Math.max(minSize, Math.min(mouseX - x, imgRight - x));
      newHeight = Math.max(minSize, Math.min(bottomEdge - mouseY, bottomEdge - imgTop));
      newY = bottomEdge - newHeight;
    } else if (handle === 'nw') {
      newWidth = Math.max(minSize, Math.min(rightEdge - mouseX, rightEdge - imgLeft));
      newHeight = Math.max(minSize, Math.min(bottomEdge - mouseY, bottomEdge - imgTop));
      newX = rightEdge - newWidth;
      newY = bottomEdge - newHeight;
    } else if (handle === 's') {
      newHeight = Math.max(minSize, Math.min(mouseY - y, imgBottom - y));
    } else if (handle === 'n') {
      newHeight = Math.max(minSize, Math.min(bottomEdge - mouseY, bottomEdge - imgTop));
      newY = bottomEdge - newHeight;
    } else if (handle === 'e') {
      newWidth = Math.max(minSize, Math.min(mouseX - x, imgRight - x));
    } else if (handle === 'w') {
      newWidth = Math.max(minSize, Math.min(rightEdge - mouseX, rightEdge - imgLeft));
      newX = rightEdge - newWidth;
    }

    this.cropBox = { x: newX, y: newY, width: newWidth, height: newHeight };
  }

  /**
   * Locked aspect-ratio resize: maintains the given width/height ratio at all times.
   */
  private resizeCropBoxLocked(mouseX: number, mouseY: number, ratio: number): void {
    const minWidth = 50;
    const { x, y, width, height } = this.cropBox;
    const handle = this.resizeHandle;

    const imgLeft = this.imagePosition.x;
    const imgTop = this.imagePosition.y;
    const imgRight = this.imagePosition.x + this.currentImage!.width * this.imageScale;
    const imgBottom = this.imagePosition.y + this.currentImage!.height * this.imageScale;

    const rightEdge = x + width;
    const bottomEdge = y + height;

    let newX = x;
    let newY = y;
    let newWidth = width;

    if (handle === 'se') {
      newWidth = Math.min(mouseX - x, imgRight - x, (imgBottom - y) * ratio);
    } else if (handle === 'sw') {
      newWidth = Math.min(rightEdge - mouseX, rightEdge - imgLeft, (imgBottom - y) * ratio);
      newX = rightEdge - newWidth;
    } else if (handle === 'ne') {
      newWidth = Math.min(mouseX - x, imgRight - x, (bottomEdge - imgTop) * ratio);
      newY = bottomEdge - newWidth / ratio;
    } else if (handle === 'nw') {
      newWidth = Math.min(rightEdge - mouseX, rightEdge - imgLeft, (bottomEdge - imgTop) * ratio);
      newX = rightEdge - newWidth;
      newY = bottomEdge - newWidth / ratio;
    } else if (handle === 'e') {
      newWidth = Math.min(mouseX - x, imgRight - x, (imgBottom - y) * ratio);
    } else if (handle === 'w') {
      newWidth = Math.min(rightEdge - mouseX, rightEdge - imgLeft, (imgBottom - y) * ratio);
      newX = rightEdge - newWidth;
    } else if (handle === 's') {
      const desiredH = Math.min(mouseY - y, imgBottom - y);
      newWidth = Math.min(desiredH * ratio, imgRight - x);
    } else if (handle === 'n') {
      const desiredH = Math.min(bottomEdge - mouseY, bottomEdge - imgTop);
      newWidth = Math.min(desiredH * ratio, imgRight - x);
      newY = bottomEdge - newWidth / ratio;
    }

    newWidth = Math.max(newWidth, minWidth);
    this.cropBox = { x: newX, y: newY, width: newWidth, height: newWidth / ratio };
  }

  /**
   * Square resize: existing behavior preserved exactly (width === height at all times).
   */
  private resizeCropBoxSquare(mouseX: number, mouseY: number): void {
    const minSize = 50;
    const { x, y, width: size } = this.cropBox;
    const handle = this.resizeHandle;

    const imgLeft = this.imagePosition.x;
    const imgTop = this.imagePosition.y;
    const imgRight = this.imagePosition.x + this.currentImage!.width * this.imageScale;
    const imgBottom = this.imagePosition.y + this.currentImage!.height * this.imageScale;

    let newX = x;
    let newY = y;
    let newSize = size;

    if (handle === 'nw' || handle === 'ne' || handle === 'sw' || handle === 'se') {
      if (handle === 'se') {
        const deltaX = Math.max(0, Math.min(mouseX - x, imgRight - x));
        const deltaY = Math.max(0, Math.min(mouseY - y, imgBottom - y));
        newSize = Math.max(deltaX, deltaY);
        newSize = Math.min(newSize, imgRight - x, imgBottom - y);
      } else if (handle === 'sw') {
        const rightEdge = x + size;
        const deltaX = Math.max(0, rightEdge - Math.max(mouseX, imgLeft));
        const deltaY = Math.max(0, Math.min(mouseY - y, imgBottom - y));
        newSize = Math.max(deltaX, deltaY);
        newSize = Math.min(newSize, rightEdge - imgLeft, imgBottom - y);
        newX = rightEdge - newSize;
      } else if (handle === 'ne') {
        const bottomEdge = y + size;
        const deltaX = Math.max(0, Math.min(mouseX - x, imgRight - x));
        const deltaY = Math.max(0, bottomEdge - Math.max(mouseY, imgTop));
        newSize = Math.max(deltaX, deltaY);
        newSize = Math.min(newSize, imgRight - x, bottomEdge - imgTop);
        newY = bottomEdge - newSize;
      } else if (handle === 'nw') {
        const rightEdge = x + size;
        const bottomEdge = y + size;
        const deltaX = Math.max(0, rightEdge - Math.max(mouseX, imgLeft));
        const deltaY = Math.max(0, bottomEdge - Math.max(mouseY, imgTop));
        newSize = Math.max(deltaX, deltaY);
        newSize = Math.min(newSize, rightEdge - imgLeft, bottomEdge - imgTop);
        newX = rightEdge - newSize;
        newY = bottomEdge - newSize;
      }
    } else {
      if (handle === 'n') {
        const deltaY = y - Math.max(mouseY, imgTop);
        newSize = size + deltaY;
        newSize = Math.min(newSize, imgRight - x);
        newY = y + size - newSize;
      } else if (handle === 's') {
        newSize = Math.min(mouseY - y, imgBottom - y);
        newSize = Math.min(newSize, imgRight - x);
      } else if (handle === 'w') {
        const rightEdge = x + size;
        const deltaX = x - Math.max(mouseX, imgLeft);
        newSize = size + deltaX;
        newSize = Math.min(newSize, imgBottom - y, rightEdge - imgLeft);
        newX = rightEdge - newSize;
      } else if (handle === 'e') {
        newSize = Math.min(mouseX - x, imgRight - x);
        newSize = Math.min(newSize, imgBottom - y);
      }
    }

    if (newSize < minSize) {
      if (handle === 'nw') {
        newSize = minSize;
        newX = (x + size) - newSize;
        newY = (y + size) - newSize;
      } else if (handle === 'ne') {
        newSize = minSize;
        newY = (y + size) - newSize;
      } else if (handle === 'sw') {
        newSize = minSize;
        newX = (x + size) - newSize;
      } else if (handle === 'n' || handle === 'w') {
        newSize = minSize;
        if (handle === 'n') newY = (y + size) - newSize;
        else newX = (x + size) - newSize;
      } else {
        newSize = minSize;
      }
    }

    this.cropBox = { x: newX, y: newY, width: newSize, height: newSize };
  }

  /**
   * Handle mouse up
   */
  onCanvasMouseUp(event?: MouseEvent): void {
    this.isDragging = false;
    this.dragMode = 'none';
    this.resizeHandle = null;

    if (event) {
      const canvas = this.cropperCanvas?.nativeElement;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const mouseX = (event.clientX - rect.left) * scaleX;
        const mouseY = (event.clientY - rect.top) * scaleY;
        this.updateCursor(mouseX, mouseY);
      }
    } else {
      this.currentCursor = 'default';
    }
  }

  /**
   * Handle touch start on canvas (mobile support)
   */
  onCanvasTouchStart(event: TouchEvent): void {
    event.preventDefault();

    const canvas = this.cropperCanvas?.nativeElement;
    if (!canvas) return;

    const touch = event.touches[0];
    const rect = canvas.getBoundingClientRect();

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const touchX = (touch.clientX - rect.left) * scaleX;
    const touchY = (touch.clientY - rect.top) * scaleY;

    const handle = this.getResizeHandle(touchX, touchY, true);
    if (handle) {
      this.isDragging = true;
      this.dragMode = 'resize';
      this.resizeHandle = handle;
      this.dragStart = { x: touchX, y: touchY };
      return;
    }

    if (this.isInsideCropBox(touchX, touchY)) {
      this.isDragging = true;
      this.dragMode = 'cropbox';
      this.dragStart = {
        x: touchX - this.cropBox.x,
        y: touchY - this.cropBox.y
      };
      return;
    }
  }

  /**
   * Handle touch move on canvas (mobile support)
   */
  onCanvasTouchMove(event: TouchEvent): void {
    if (!this.isDragging) return;

    event.preventDefault();

    const canvas = this.cropperCanvas?.nativeElement;
    if (!canvas) return;

    const touch = event.touches[0];
    const rect = canvas.getBoundingClientRect();

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const touchX = (touch.clientX - rect.left) * scaleX;
    const touchY = (touch.clientY - rect.top) * scaleY;

    if (this.dragMode === 'cropbox') {
      this.dragCropBox(touchX, touchY);
    } else if (this.dragMode === 'resize' && this.resizeHandle) {
      this.resizeCropBox(touchX, touchY);
    }

    this.drawCropper();
  }

  /**
   * Handle touch end (mobile support)
   */
  onCanvasTouchEnd(event: TouchEvent): void {
    event.preventDefault();
    this.isDragging = false;
    this.dragMode = 'none';
    this.resizeHandle = null;
  }

  /**
   * Apply crop and emit result or upload.
   * Square mode: output is outputSize × outputSize.
   * Free-aspect-ratio mode: longest dimension = outputSize, other dimension is proportional.
   */
  applyCrop(): void {
    const canvas = this.cropperCanvas?.nativeElement;
    if (!canvas || !this.currentImage) {
      console.error('Canvas or image not available for cropping');
      return;
    }

    try {
      const { width: cropWidth, height: cropHeight } = this.cropBox;

      // Calculate output canvas dimensions
      let outWidth: number;
      let outHeight: number;
      if (this.cropMode === 'free') {
        const ratio = cropWidth / cropHeight;
        if (ratio >= 1) {
          outWidth = this.outputSize;
          outHeight = Math.round(this.outputSize / ratio);
        } else {
          outHeight = this.outputSize;
          outWidth = Math.round(this.outputSize * ratio);
        }
      } else if (this.cropMode === 'banner') {
        outWidth = this.BANNER_OUT_WIDTH;
        outHeight = this.BANNER_OUT_HEIGHT;
      } else {
        outWidth = this.outputSize;
        outHeight = this.outputSize;
      }

      const croppedCanvas = document.createElement('canvas');
      croppedCanvas.width = outWidth;
      croppedCanvas.height = outHeight;
      const ctx = croppedCanvas.getContext('2d');

      if (!ctx) {
        console.error('Failed to get canvas context');
        return;
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Calculate the source rectangle from the crop box
      const sourceX = this.cropBox.x - this.imagePosition.x;
      const sourceY = this.cropBox.y - this.imagePosition.y;

      // Draw the cropped portion scaled to output size
      ctx.drawImage(
        this.currentImage,
        sourceX / this.imageScale,
        sourceY / this.imageScale,
        cropWidth / this.imageScale,
        cropHeight / this.imageScale,
        0,
        0,
        outWidth,
        outHeight
      );

      const isLossless = this.imageSourceType === 'image/png' || this.imageSourceType === 'image/gif';
      const quality = isLossless ? undefined : this.quality;

      croppedCanvas.toBlob((blob) => {
        if (!blob) {
          console.error('Failed to create blob from canvas');
          return;
        }

        if (this.uploadFn) {
          this.isUploading = true;
          this.uploadFn(blob).subscribe({
            next: (response) => {
              this.isUploading = false;
              this.uploadSuccess.emit(response);
              this.close();
            },
            error: (error) => {
              this.isUploading = false;
              this.uploadError.emit(error);
            }
          });
        } else {
          const dataUrl = croppedCanvas.toDataURL(this.imageSourceType, quality);
          this.imageCropped.emit(dataUrl);
          this.imageCroppedBlob.emit(blob);
          this.close();
        }
      }, this.imageSourceType, quality);
    } catch (error) {
      console.error('Error cropping image:', error);
    }
  }
}
